import type { Edge, Node } from "@xyflow/react";
import type {
  WorkspaceDetail,
  WorkspaceEdge,
  WorkspaceNode,
  WorkspaceNodeType,
} from "../../../types/workspace";
import {
  WORKSPACE_ACCOUNT_CARD_SIZE,
  WORKSPACE_NODE_CARD_HEIGHT,
  WORKSPACE_NODE_CARD_WIDTH,
} from "./workspaceGraphConstants";
import { resolveWorkspaceNodePosition } from "./workspaceLayoutUtils";
import {
  isOptimisticWorkspaceReportNodeId,
  isPendingOptimisticReportEntityId,
} from "./workspaceOptimisticReport";

export const WORKSPACE_NODE_TYPE_LABELS: Record<WorkspaceNodeType, string> = {
  account: "Account",
  strategy: "Strategy",
  report: "Report",
  simulation: "Simulation",
  policy: "Policy",
  combined_policy: "Combined Policy",
  combined_simulation: "Combined Simulation",
};

const WORKSPACE_NODE_TYPE_SUMMARIES: Record<
  Exclude<WorkspaceNodeType, "account">,
  string
> = {
  strategy: "Rules and PineScript for this account pipeline.",
  report: "Analysis and findings on a trade cohort or window.",
  simulation: "Backtest evaluating report data with policy rules.",
  policy: "Saved policy version from a simulation run.",
  combined_policy: "Merged policy bundle from two or more single policies.",
  combined_simulation: "Simulation run against a combined policy bundle.",
};

import type {
  WorkspaceChildActionKind,
  WorkspaceChildAddMode,
} from "./workspaceChildActions";
import { findWorkspaceDisconnectTarget, listWorkspaceReparentCandidates } from "./workspaceReparentUtils";
import {
  findParentCombinedPolicyNode,
  listCombinedPolicySourceNodes,
} from "./workspaceCombinedPolicyUtils";
import { workspaceNodeIsPolicyFork } from "./workspacePolicyForkUtils";
import {
  workspaceEntityCardDetailKey,
  type WorkspaceEntityCardDetail,
  type WorkspaceEntityCardDetailsMap,
} from "./workspaceEntityCardDetails";

export { workspaceNodeIsPolicyFork };

export type WorkspaceFlowNodeData = {
  workspaceNode: WorkspaceNode;
  label: string;
  summary: string;
  entityCardDetail?: WorkspaceEntityCardDetail | null;
  /** For policy nodes: the upstream strategy entityId needed to fetch the policy version. */
  policyStrategyId?: string | null;
  selected?: boolean;
  /** Simulation stacked under another simulation (date-window rerun). */
  isSimulationRerun?: boolean;
  /** Policy / combined_policy forked via Edit & run under a source policy. */
  isPolicyFork?: boolean;
  /** First policy in a combine multi-select (adopts report window). */
  combineSelectionAnchor?: boolean;
  /** Incompatible policy while a combine multi-select is active. */
  combineSelectionDimmed?: boolean;
  /** Policy that feeds the currently selected combined_policy. */
  combinedSourceHighlight?: boolean;
  /** Canvas focus filter — grey out non-matching nodes (like disabled). */
  focusFilterDimmed?: boolean;
  showActionsMenu?: boolean;
  /** Temporary neon-aqua completion flash after report generation. */
  completionFlash?: boolean;
  /** Optimistic create failed — fade the placeholder off the canvas. */
  dismissing?: boolean;
  accountLabel?: string;
  mutating?: boolean;
  canDisconnect?: boolean;
  reparentCandidates?: { node: WorkspaceNode; label: string }[];
  viewMode?: "account" | "strategy";
  onClearSelection?: () => void;
  onSelectNode?: () => void;
  onAddChild?: (
    node: WorkspaceNode,
    kind: WorkspaceChildActionKind,
    mode?: WorkspaceChildAddMode
  ) => void;
  onCombinePolicies?: (node: WorkspaceNode) => void;
  onRunCombinedSimulation?: (node: WorkspaceNode) => void;
  onRerunSimulation?: (node: WorkspaceNode) => void;
  onEditSimulationInSimulator?: (node: WorkspaceNode) => void;
  onAddExecutingAccount?: (node: WorkspaceNode) => void;
  onOpenDetail?: (node: WorkspaceNode) => void;
  onOpenStrategyWorkspace?: (node: WorkspaceNode) => void;
  onOpenOnAccountCanvas?: (node: WorkspaceNode) => void;
  onToggleDisabled?: (node: WorkspaceNode) => void;
  onDisconnect?: (node: WorkspaceNode) => void;
  onReparent?: (node: WorkspaceNode, newParent: WorkspaceNode) => void;
  onBreakLink?: (node: WorkspaceNode) => void;
};

export type WorkspaceFlowNodeOptions = {
  accountLabel?: string;
  mutating?: boolean;
  actionsMenuNodeId?: string | null;
  viewMode?: "account" | "strategy";
  /** Entity ids that should show a temporary completion flash. */
  completionFlashEntityIds?: ReadonlySet<string>;
  /** Node ids fading out after a failed optimistic create. */
  dismissingNodeIds?: ReadonlySet<string>;
  /** Ordered multi-select ids (policy combine). */
  selectedNodeIds?: string[];
  /** Same-strategy policy ids eligible to join the current combine selection. */
  combinablePolicyIds?: ReadonlySet<string> | null;
  /** Node ids that should stay fully visible under the canvas focus filter. */
  focusMatchedNodeIds?: ReadonlySet<string> | null;
  edges?: WorkspaceEdge[];
  reportTitlesByEntityId?: Record<string, string>;
  strategyTitlesByEntityId?: Record<string, string>;
  simulationTitlesByEntityId?: Record<string, string>;
  /** Backtest report_id (or last known report parent) keyed by simulation entity id. */
  simulationSourceReportIdByEntityId?: Record<string, string>;
  entityCardDetailsByKey?: WorkspaceEntityCardDetailsMap;
  onClearSelection?: () => void;
  onSelectNode?: (nodeId: string) => void;
  onAddChild?: (
    node: WorkspaceNode,
    kind: WorkspaceChildActionKind,
    mode?: WorkspaceChildAddMode
  ) => void;
  onCombinePolicies?: (node: WorkspaceNode) => void;
  onRunCombinedSimulation?: (node: WorkspaceNode) => void;
  onRerunSimulation?: (node: WorkspaceNode) => void;
  onEditSimulationInSimulator?: (node: WorkspaceNode) => void;
  onAddExecutingAccount?: (node: WorkspaceNode) => void;
  onOpenDetail?: (node: WorkspaceNode) => void;
  onOpenStrategyWorkspace?: (node: WorkspaceNode) => void;
  onOpenOnAccountCanvas?: (node: WorkspaceNode) => void;
  onToggleDisabled?: (node: WorkspaceNode) => void;
  onDisconnect?: (node: WorkspaceNode) => void;
  onReparent?: (node: WorkspaceNode, newParent: WorkspaceNode) => void;
  onBreakLink?: (node: WorkspaceNode) => void;
};

const GENERIC_REPORT_META_TITLES = new Set(["report", "new report", "untitled"]);
const GENERIC_TYPE_META_TITLES = new Set([
  "strategy",
  "report",
  "simulation",
  "policy",
  "combined policy",
  "combined simulation",
  "combined_policy",
  "combined_simulation",
  "untitled",
]);

function isGenericReportMetaTitle(title: string): boolean {
  return GENERIC_REPORT_META_TITLES.has(title.trim().toLowerCase());
}

function isGenericTypeMetaTitle(type: WorkspaceNodeType, title: string): boolean {
  const normalized = title.trim().toLowerCase();
  if (type === "report") return isGenericReportMetaTitle(normalized);
  return GENERIC_TYPE_META_TITLES.has(normalized);
}

export function workspaceNodeTitle(
  node: WorkspaceNode,
  options?: {
    accountLabel?: string;
    reportTitlesByEntityId?: Record<string, string>;
    strategyTitlesByEntityId?: Record<string, string>;
    simulationTitlesByEntityId?: Record<string, string>;
  }
): string {
  if (node.type === "report") {
    const reportTitle = options?.reportTitlesByEntityId?.[node.entityId]?.trim();
    if (reportTitle) return reportTitle;
  }
  if (node.type === "strategy") {
    const strategyTitle = options?.strategyTitlesByEntityId?.[node.entityId]?.trim();
    if (strategyTitle) return strategyTitle;
  }
  if (node.type === "simulation") {
    const simulationTitle = options?.simulationTitlesByEntityId?.[node.entityId]?.trim();
    if (simulationTitle) return simulationTitle;
  }

  const metaTitle = node.meta?.title?.trim();
  if (metaTitle && !isGenericTypeMetaTitle(node.type, metaTitle)) {
    return metaTitle;
  }
  if (node.type === "account") {
    return options?.accountLabel?.trim() || node.entityId?.trim() || WORKSPACE_NODE_TYPE_LABELS.account;
  }
  return WORKSPACE_NODE_TYPE_LABELS[node.type];
}

export function workspaceNodeSummary(node: WorkspaceNode): string {
  const subtitle = node.meta?.subtitle?.trim();
  if (subtitle) return subtitle;
  if (node.type === "account") return "";
  if (
    (node.type === "simulation" || node.type === "combined_simulation") &&
    (node.meta?.isRerun || node.meta?.rerunOfEntityId)
  ) {
    return "Date-window rerun stacked under the original simulation.";
  }
  if (
    (node.type === "policy" || node.type === "combined_policy") &&
    (node.meta?.isFork || node.meta?.forkedFromEntityId)
  ) {
    return "Edited fork of a source policy (Edit & run).";
  }
  return WORKSPACE_NODE_TYPE_SUMMARIES[node.type];
}

/** True when this simulation is a stacked date-window rerun of another simulation. */
export function workspaceNodeIsSimulationRerun(
  node: WorkspaceNode,
  edges: WorkspaceEdge[] = [],
  nodes: WorkspaceNode[] = []
): boolean {
  if (node.type !== "simulation" && node.type !== "combined_simulation") return false;
  if (node.meta?.isRerun || node.meta?.rerunOfEntityId) return true;
  if (edges.length === 0 || nodes.length === 0) return false;
  const nodeById = new Map(nodes.map((entry) => [entry.id, entry]));
  return edges.some((edge) => {
    if (edge.targetNodeId !== node.id) return false;
    const parent = nodeById.get(edge.sourceNodeId);
    if (!parent) return false;
    // Regular sim stacked under sim, or combined sim stacked under combined sim.
    return parent.type === node.type;
  });
}

function resolveFlowPosition(node: WorkspaceNode): WorkspaceNode["position"] {
  return resolveWorkspaceNodePosition(node);
}

export function toFlowNodes(
  nodes: WorkspaceNode[],
  selectedNodeId: string | null,
  options?: WorkspaceFlowNodeOptions
): Array<Node<WorkspaceFlowNodeData>> {
  const edges = options?.edges ?? [];
  const selectedIds = options?.selectedNodeIds?.length
    ? options.selectedNodeIds
    : selectedNodeId
      ? [selectedNodeId]
      : [];
  const selectedSet = new Set(selectedIds);
  const combinable = options?.combinablePolicyIds ?? null;
  const combineAnchorId = selectedIds[0] ?? null;
  const combineMode = Boolean(combinable && combinable.size > 0 && selectedIds.length > 0);

  // When a single combined_policy (or its combined_simulation) is selected,
  // highlight its source policies.
  const selectedCombinedPolicy =
    !combineMode && selectedIds.length === 1
      ? nodes.find((node) => node.id === selectedIds[0] && node.type === "combined_policy") ?? null
      : null;
  const selectedCombinedSimulation =
    !combineMode && selectedIds.length === 1
      ? nodes.find((node) => node.id === selectedIds[0] && node.type === "combined_simulation") ??
        null
      : null;
  const combinedPolicyForSourceHighlight =
    selectedCombinedPolicy ??
    (selectedCombinedSimulation
      ? findParentCombinedPolicyNode(selectedCombinedSimulation, nodes, edges)
      : null);
  const combinedSourceIds = combinedPolicyForSourceHighlight
    ? new Set(
        listCombinedPolicySourceNodes(combinedPolicyForSourceHighlight, nodes, edges).map(
          (node) => node.id
        )
      )
    : null;

  return nodes.map((workspaceNode) => {
    const isAccount = workspaceNode.type === "account";
    const reparentCandidates = listWorkspaceReparentCandidates(workspaceNode, nodes, edges, {
      simulationSourceReportIdByEntityId: options?.simulationSourceReportIdByEntityId,
    }).map((candidate) => ({
      node: candidate,
      label: workspaceNodeTitle(candidate, options),
    }));
    const canDisconnect = Boolean(
      findWorkspaceDisconnectTarget(workspaceNode, nodes, edges)
    );
    const isSelected = selectedSet.has(workspaceNode.id);
    const combineSelectionAnchor =
      combineMode &&
      workspaceNode.id === combineAnchorId &&
      (workspaceNode.type === "policy" || workspaceNode.type === "combined_policy");
    const combineSelectionDimmed =
      combineMode &&
      (workspaceNode.type === "policy" || workspaceNode.type === "combined_policy") &&
      !combinable!.has(workspaceNode.id);
    const combinedSourceHighlight = Boolean(
      combinedSourceIds && combinedSourceIds.has(workspaceNode.id)
    );
    const focusMatched = options?.focusMatchedNodeIds;
    const focusFilterDimmed = Boolean(
      focusMatched && focusMatched.size > 0 && !focusMatched.has(workspaceNode.id)
    );
    return {
      id: workspaceNode.id,
      type: "workspace",
      position: resolveFlowPosition(workspaceNode),
      width: isAccount ? WORKSPACE_ACCOUNT_CARD_SIZE : WORKSPACE_NODE_CARD_WIDTH,
      height: isAccount ? WORKSPACE_ACCOUNT_CARD_SIZE : WORKSPACE_NODE_CARD_HEIGHT,
      selected: isSelected,
      data: {
        workspaceNode,
        label: workspaceNodeTitle(workspaceNode, options),
        summary: workspaceNodeSummary(workspaceNode),
        entityCardDetail:
          options?.entityCardDetailsByKey?.[
            workspaceEntityCardDetailKey(workspaceNode.type, workspaceNode.entityId)
          ] ?? null,
        policyStrategyId:
          workspaceNode.type === "policy"
            ? findUpstreamStrategyEntityId(workspaceNode.id, nodes, edges) ?? null
            : null,
        selected: isSelected,
        isSimulationRerun: workspaceNodeIsSimulationRerun(workspaceNode, edges, nodes),
        isPolicyFork: workspaceNodeIsPolicyFork(workspaceNode, edges, nodes),
        combineSelectionAnchor,
        combineSelectionDimmed,
        combinedSourceHighlight,
        focusFilterDimmed,
        showActionsMenu: options?.actionsMenuNodeId === workspaceNode.id,
        completionFlash: Boolean(
          options?.completionFlashEntityIds?.has(workspaceNode.entityId.trim())
        ),
        dismissing: Boolean(options?.dismissingNodeIds?.has(workspaceNode.id)),
        accountLabel: options?.accountLabel,
        mutating: options?.mutating,
        canDisconnect,
        reparentCandidates,
        viewMode: options?.viewMode ?? "account",
        onClearSelection: options?.onClearSelection,
        onSelectNode: () => options?.onSelectNode?.(workspaceNode.id),
        onAddChild: options?.onAddChild,
        onCombinePolicies: options?.onCombinePolicies,
        onRunCombinedSimulation: options?.onRunCombinedSimulation,
        onRerunSimulation: options?.onRerunSimulation,
        onEditSimulationInSimulator: options?.onEditSimulationInSimulator,
        onAddExecutingAccount: options?.onAddExecutingAccount,
        onOpenDetail: options?.onOpenDetail,
        onOpenStrategyWorkspace: options?.onOpenStrategyWorkspace,
        onOpenOnAccountCanvas: options?.onOpenOnAccountCanvas,
        onToggleDisabled: options?.onToggleDisabled,
        onDisconnect: options?.onDisconnect,
        onReparent: options?.onReparent,
        onBreakLink: options?.onBreakLink,
      },
      draggable: !isAccount,
      selectable: true,
    };
  });
}

export function workspaceNodeStatusLabel(node: WorkspaceNode): string | null {
  const status = node.meta?.status;
  if (!status) return null;
  if (status === "complete") return "Complete";
  if (status === "processing") return "Processing";
  if (status === "failed") return "Failed";
  if (status === "saved") return "Saved";
  if (status === "draft") return "Draft";
  if (status === "published") return "Published";
  if (status === "archived") return "Archived";
  return status;
}

/** Canvas pills are for in-progress / problem states — not quiet success statuses. */
export function shouldShowWorkspaceStatusPill(status?: string | null): boolean {
  if (!status) return false;
  return status === "processing" || status === "failed" || status === "draft";
}

/** Edges on the path from the graph root down to a loading/processing node. */
function collectLoadingPathEdgeIds(
  edges: WorkspaceEdge[],
  nodes: WorkspaceNode[]
): Set<string> {
  const loadingNodeIds = new Set(
    nodes
      .filter((node) => node.meta?.status === "processing")
      .map((node) => node.id)
  );
  if (loadingNodeIds.size === 0) return new Set();

  const edgeIdByTarget = new Map<string, string>();
  const parentByTarget = new Map<string, string>();
  for (const edge of edges) {
    edgeIdByTarget.set(edge.targetNodeId, edge.id);
    parentByTarget.set(edge.targetNodeId, edge.sourceNodeId);
  }

  const pathEdgeIds = new Set<string>();
  Array.from(loadingNodeIds).forEach((nodeId) => {
    let current: string | undefined = nodeId;
    const seen = new Set<string>();
    while (current && !seen.has(current)) {
      seen.add(current);
      const edgeId = edgeIdByTarget.get(current);
      if (edgeId) pathEdgeIds.add(edgeId);
      current = parentByTarget.get(current);
    }
  });
  return pathEdgeIds;
}

/** Edge shape accepted for selection-path walks (persisted or soft/visual). */
export type SelectionPathEdgeRef = {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
};

/**
 * Orange selection path: every ancestor path into `nodeId` (all inbound edges at
 * each hop — not just the first parent), plus direct children edges
 * (e.g. policy → combined_policy).
 *
 * `extraEdges` covers Sequential soft hops (policy → fed account / diamond
 * continuation) that are not in persisted `edges`.
 */
export function collectSelectionPathEdgeIds(
  nodeId: string,
  edges: WorkspaceEdge[],
  extraEdges: readonly SelectionPathEdgeRef[] = []
): Set<string> {
  const pathEdgeIds = new Set<string>();
  const inboundByTarget = new Map<string, SelectionPathEdgeRef[]>();
  const register = (edge: SelectionPathEdgeRef) => {
    const list = inboundByTarget.get(edge.targetNodeId) ?? [];
    list.push(edge);
    inboundByTarget.set(edge.targetNodeId, list);
  };
  for (const edge of edges) register(edge);
  for (const edge of extraEdges) register(edge);

  const stack = [nodeId];
  const seen = new Set<string>();
  while (stack.length > 0) {
    const current = stack.pop()!;
    if (seen.has(current)) continue;
    seen.add(current);
    for (const edge of inboundByTarget.get(current) ?? []) {
      pathEdgeIds.add(edge.id);
      stack.push(edge.sourceNodeId);
    }
  }

  for (const edge of edges) {
    if (edge.sourceNodeId === nodeId) pathEdgeIds.add(edge.id);
  }
  for (const edge of extraEdges) {
    if (edge.sourceNodeId === nodeId) pathEdgeIds.add(edge.id);
  }
  return pathEdgeIds;
}

/** Node ids on the selection path (selected node + all ancestors + direct children). */
export function collectSelectionPathNodeIds(
  nodeId: string,
  edges: WorkspaceEdge[],
  extraEdges: readonly SelectionPathEdgeRef[] = []
): Set<string> {
  const pathEdgeIds = collectSelectionPathEdgeIds(nodeId, edges, extraEdges);
  const nodeIds = new Set<string>([nodeId]);
  const consider = (edge: SelectionPathEdgeRef) => {
    if (!pathEdgeIds.has(edge.id)) return;
    nodeIds.add(edge.sourceNodeId);
    nodeIds.add(edge.targetNodeId);
  };
  for (const edge of edges) consider(edge);
  for (const edge of extraEdges) consider(edge);
  return nodeIds;
}

export function toFlowEdges(
  edges: WorkspaceEdge[],
  nodes: WorkspaceNode[],
  options?: {
    skipEdgeIds?: Set<string>;
    /** When set, edges that don't touch a matched node are dimmed. */
    focusMatchedNodeIds?: ReadonlySet<string> | null;
    /** Highlight edges from source policies into this combined_policy. */
    combinedSourceHighlight?: {
      combinedPolicyNodeId: string;
      sourcePolicyNodeIds: ReadonlySet<string>;
    } | null;
    /** Orange path highlight for the selected policy / combined_policy. */
    selectionPathEdgeIds?: ReadonlySet<string> | null;
  }
): Edge[] {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const focusMatched = options?.focusMatchedNodeIds ?? null;
  const combinedSource = options?.combinedSourceHighlight ?? null;
  const selectionPathEdgeIds = options?.selectionPathEdgeIds ?? null;

  const visible = edges.filter((edge) => !options?.skipEdgeIds?.has(edge.id));
  const loadingPathEdgeIds = collectLoadingPathEdgeIds(visible, nodes);

  // Assign vertical lanes per source so fan-out edges don't share one corridor.
  const lanesByEdgeId = new Map<string, { lane: number; laneCount: number }>();
  const bySource = new Map<string, WorkspaceEdge[]>();
  for (const edge of visible) {
    const list = bySource.get(edge.sourceNodeId) ?? [];
    list.push(edge);
    bySource.set(edge.sourceNodeId, list);
  }
  Array.from(bySource.values()).forEach((group) => {
    const sorted = [...group].sort((a, b) => {
      const aNode = nodeById.get(a.targetNodeId);
      const bNode = nodeById.get(b.targetNodeId);
      const aY = aNode?.position.y ?? 0;
      const bY = bNode?.position.y ?? 0;
      if (aY !== bY) return aY - bY;
      return a.targetNodeId.localeCompare(b.targetNodeId);
    });
    sorted.forEach((edge, lane) => {
      lanesByEdgeId.set(edge.id, { lane, laneCount: sorted.length });
    });
  });

  return visible.map((edge) => {
    const onLoadingPath = loadingPathEdgeIds.has(edge.id);
    const laneMeta = lanesByEdgeId.get(edge.id);
    const sourceNode = nodeById.get(edge.sourceNodeId);
    const targetNode = nodeById.get(edge.targetNodeId);
    const isSimRerunEdge =
      (sourceNode?.type === "simulation" && targetNode?.type === "simulation") ||
      (sourceNode?.type === "combined_simulation" &&
        targetNode?.type === "combined_simulation");
    /** Derived Edit & run: source policy/CP → new policy/CP under it. */
    const isDerivedPolicyLineageEdge =
      (sourceNode?.type === "policy" && targetNode?.type === "policy") ||
      (sourceNode?.type === "combined_policy" &&
        targetNode?.type === "combined_policy");
    const focusFilterDimmed = Boolean(
      focusMatched &&
        focusMatched.size > 0 &&
        !focusMatched.has(edge.sourceNodeId) &&
        !focusMatched.has(edge.targetNodeId)
    );
    const combinedSourceHighlight = Boolean(
      (combinedSource &&
        edge.targetNodeId === combinedSource.combinedPolicyNodeId &&
        combinedSource.sourcePolicyNodeIds.has(edge.sourceNodeId)) ||
        selectionPathEdgeIds?.has(edge.id)
    );

    return {
      id: edge.id,
      source: edge.sourceNodeId,
      target: edge.targetNodeId,
      sourceHandle: isSimRerunEdge ? "source-bottom" : "source",
      targetHandle: isSimRerunEdge ? "target-top" : "target",
      type: "workspacePipeline",
      animated: onLoadingPath,
      // Keep amber combined-source / selection paths above trunk ribbons.
      zIndex: combinedSourceHighlight ? 1001 : onLoadingPath ? 500 : 0,
      data: {
        loadingPath: onLoadingPath,
        verticalStack: isSimRerunEdge,
        centerAim: isDerivedPolicyLineageEdge,
        focusFilterDimmed,
        combinedSourceHighlight,
        ...(laneMeta ?? {}),
      },
    };
  });
}

export function findWorkspaceNodeByEntity(
  detail: WorkspaceDetail,
  type: WorkspaceNode["type"],
  entityId: string
): WorkspaceNode | null {
  const normalized = entityId.trim();
  if (!normalized) return null;
  return (
    detail.nodes.find((node) => {
      if (node.type !== type || node.entityId !== normalized) return false;
      // Ignore pure pending placeholders; adopted optimistic Flow ids (stamped
      // with a real entity id) are valid canvas nodes.
      if (
        isOptimisticWorkspaceReportNodeId(node.id) &&
        isPendingOptimisticReportEntityId(node.entityId)
      ) {
        return false;
      }
      return true;
    }) ?? null
  );
}

/**
 * Resolve a `?node=type/entityId` deep link. Hub sim rows may deep-link as
 * `simulation` even when the canvas node is `combined_simulation` (and vice versa).
 */
export function findWorkspaceNodeForDeepLink(
  detail: WorkspaceDetail,
  type: WorkspaceNode["type"],
  entityId: string
): WorkspaceNode | null {
  const exact = findWorkspaceNodeByEntity(detail, type, entityId);
  if (exact) return exact;
  if (type === "simulation") {
    return findWorkspaceNodeByEntity(detail, "combined_simulation", entityId);
  }
  if (type === "combined_simulation") {
    return findWorkspaceNodeByEntity(detail, "simulation", entityId);
  }
  if (type === "policy") {
    return findWorkspaceNodeByEntity(detail, "combined_policy", entityId);
  }
  if (type === "combined_policy") {
    return findWorkspaceNodeByEntity(detail, "policy", entityId);
  }
  return null;
}

export function mergeWorkspaceDetail(
  current: WorkspaceDetail,
  patch: Partial<Pick<WorkspaceDetail, "nodes" | "edges" | "viewport" | "name">>
): WorkspaceDetail {
  return {
    ...current,
    ...patch,
    nodes: patch.nodes ?? current.nodes,
    edges: patch.edges ?? current.edges,
  };
}

/** Walk parent edges until a strategy node is found; returns its entityId. */
export function findUpstreamStrategyEntityId(
  nodeId: string,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): string | null {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const parentsByTarget = new Map<string, string[]>();
  for (const edge of edges) {
    const list = parentsByTarget.get(edge.targetNodeId) ?? [];
    list.push(edge.sourceNodeId);
    parentsByTarget.set(edge.targetNodeId, list);
  }

  const queue = [nodeId];
  const seen = new Set<string>();
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const node = byId.get(id);
    if (node?.type === "strategy" && node.entityId.trim()) {
      return node.entityId.trim();
    }
    for (const parentId of parentsByTarget.get(id) ?? []) {
      queue.push(parentId);
    }
  }
  return null;
}
